'use client'

import { useState } from 'react'
import { toast } from '@dd3tech/wiggui/Toast'
import { getNotifications, patchRemoveCountNotifications } from '../services'
import { useNotificationStore } from '../store/notifications-store'
import { useSession } from '@wiggot/auth/hooks/useSession'
import useRoles from '@wiggot/auth/hooks/useRoles'

export const useNotifications = () => {
  const { profile } = useSession()
  const { isBasicUser } = useRoles()

  const [isLoadingRemove, setIsLoadingRemove] = useState(false)

  const userId = profile?.userId?.toString() ?? ''
  const isLoadingNotifications = useNotificationStore(
    (state) => state.isLoading
  )
  const setIsLoadingNotifications = useNotificationStore(
    (state) => state.setIsLoading
  )

  const metadata = useNotificationStore((state) => state.metadata)
  const notifications = useNotificationStore((state) => state.notifications)
  const setNotifications = useNotificationStore(
    (state) => state.setNotifications
  )
  const isErrorGetNotifications = useNotificationStore(
    (state) => state.isErrorGetNotifications
  )
  const setIsErrorGetNotifications = useNotificationStore(
    (state) => state.setIsErrorGetNotifications
  )
  const setUnreadNotifications = useNotificationStore(
    (state) => state.setUnreadNotifications
  )

  const setMetadata = useNotificationStore((state) => state.setMetadata)

  const removeCountFromNotifications = async () => {
    try {
      setIsLoadingRemove(true)
      const { isSuccess } = await patchRemoveCountNotifications({
        userId
      })

      if (isSuccess) {
        setUnreadNotifications(0)
        toast({
          type: 'success',
          title: 'Todas las notificaciones han sido marcadas como leídas'
        })
      }
    } finally {
      setIsLoadingRemove(false)
    }
  }

  const fetchNotifications = async ({ page }: { page: number }) => {
    if (isBasicUser) {
      setIsErrorGetNotifications({
        status: true,
        message: 'No tienes permisos para visualizar las notificaciones'
      })
      return
    }

    try {
      setIsLoadingNotifications(true)
      const { data, isSuccess, error } = await getNotifications({
        userId,
        page,
        limit: 15
      })
      if (isSuccess && data) {
        setIsErrorGetNotifications({
          status: false,
          message: ''
        })
        setMetadata({
          page: data?.page,
          pages: data?.pages - 1,
          totalNotifications: data?.total,
          unread: data?.unread
        })
        if (notifications && page !== 0) {
          setNotifications([...notifications, ...data?.notifications])
          return
        }

        setNotifications(data?.notifications)
        return
      }
      if ([401, 404].includes(error?.status)) {
        setIsErrorGetNotifications({
          status: true,
          message: 'Ocurrio un error al cargar las notificaciones'
        })
        toast({
          type: 'error',
          title: 'Ocurrió un error al cargar las notificaciones'
        })
      }
    } catch (error) {
      setIsErrorGetNotifications({
        status: true,
        message: 'Ocurrio un error al cargar las notificaciones'
      })
      toast({
        type: 'error',
        title: 'Ocurrió un error al cargar las notificaciones'
      })
    } finally {
      setIsLoadingNotifications(false)
    }
  }

  return {
    removeCountFromNotifications,
    fetchNotifications,
    isErrorGetNotifications,
    isLoadingRemove,
    isLoadingNotifications,
    notifications,
    unreadNotifications: metadata.unread,
    metadata
  }
}
